const DROPBOX_SDK_URL = 'https://www.dropbox.com/static/api/2/dropins.js';
const DROPBOX_APP_KEY = '6yfyq1hblo01sn3';
const DROPBOX_SCRIPT_ID = 'dropboxjs';
const DROPBOX_LOAD_TIMEOUT = 15000;
export function loadDropboxSdk() {
    return new Promise((resolve, reject) => {
        if (window.Dropbox) {
            resolve();
            return;
        }
        let settled = false;
        const timeoutId = setTimeout(() => {
            if (!settled) {
                settled = true;
                if (pollInterval)
                    clearInterval(pollInterval);
                reject(new Error('Dropbox SDK load timeout'));
            }
        }, DROPBOX_LOAD_TIMEOUT);
        const existingScript = document.getElementById(DROPBOX_SCRIPT_ID);
        const pollInterval = setInterval(() => {
            if (window.Dropbox && !settled) {
                settled = true;
                clearInterval(pollInterval);
                clearTimeout(timeoutId);
                resolve();
            }
        }, 100);
        if (!existingScript) {
            const script = document.createElement('script');
            script.id = DROPBOX_SCRIPT_ID;
            script.type = 'text/javascript';
            script.src = DROPBOX_SDK_URL;
            script.setAttribute('data-app-key', DROPBOX_APP_KEY);
            script.onerror = () => {
                if (!settled) {
                    settled = true;
                    clearInterval(pollInterval);
                    clearTimeout(timeoutId);
                    reject(new Error('Failed to load Dropbox SDK script'));
                }
            };
            document.body.appendChild(script);
        }
    });
}
export async function isDropboxAvailable() {
    try {
        await loadDropboxSdk();
        const available = !!window.Dropbox;
        console.log('[CloudImport] isDropboxAvailable:', available, '| window.Dropbox:', typeof window.Dropbox, '| isBrowserSupported:', window.Dropbox?.isBrowserSupported?.() ?? 'N/A');
        return available;
    }
    catch (err) {
        console.warn('[CloudImport] isDropboxAvailable failed:', err);
        return false;
    }
}
export async function openDropboxChooser(options) {
    try {
        await loadDropboxSdk();
    }
    catch (err) {
        console.error('[CloudImport] Failed to load Dropbox SDK for chooser:', err);
        return null;
    }
    return new Promise((resolve) => {
        if (!window.Dropbox) {
            console.error('[CloudImport] Dropbox SDK not loaded after loadDropboxSdk');
            resolve(null);
            return;
        }
        window.Dropbox.choose({
            success: (files) => {
                console.log('[CloudImport] Dropbox files selected:', files.length);
                resolve(files);
            },
            cancel: () => {
                console.log('[CloudImport] Dropbox chooser cancelled');
                resolve(null);
            },
            linkType: "direct",
            multiselect: options?.multiselect ?? true,
            extensions: options?.extensions,
        });
    });
}
export async function downloadAndUploadDropboxFile(dropboxFile, targetPath, uploadFn) {
    try {
        console.log('[CloudImport] Downloading from Dropbox:', dropboxFile.name);
        const response = await fetch(dropboxFile.link);
        if (!response.ok) {
            return { success: false, fileName: dropboxFile.name, error: `Download failed: HTTP ${response.status}` };
        }
        const blob = await response.blob();
        const file = new File([blob], dropboxFile.name, {
            type: blob.type || 'application/octet-stream',
            lastModified: Date.now(),
        });
        console.log('[CloudImport] Uploading to storage:', targetPath + '/' + dropboxFile.name);
        const result = await uploadFn(file, targetPath + '/' + dropboxFile.name);
        if (result.success) {
            return { success: true, fileName: dropboxFile.name };
        }
        else {
            return { success: false, fileName: dropboxFile.name, error: result.error || 'Upload failed' };
        }
    }
    catch (error) {
        return {
            success: false,
            fileName: dropboxFile.name,
            error: error instanceof Error ? error.message : 'Import failed',
        };
    }
}
export async function importFromDropbox(targetPath, uploadFn, onProgress) {
    const files = await openDropboxChooser({ multiselect: true });
    if (!files || files.length === 0) {
        return [];
    }
    const results = [];
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        onProgress?.(i + 1, files.length, file.name);
        const result = await downloadAndUploadDropboxFile(file, targetPath, uploadFn);
        results.push(result);
    }
    return results;
}
function generateRandomString(length) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, b => chars[b % chars.length]).join('');
}
async function generateCodeChallenge(verifier) {
    const data = new TextEncoder().encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(hash)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export async function startDropboxOAuth() {
    const codeVerifier = generateRandomString(64);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateRandomString(16);
    sessionStorage.setItem('dropbox_pkce_verifier', codeVerifier);
    sessionStorage.setItem('dropbox_pkce_state', state);
    const redirectUri = `${window.location.origin}/dropbox-callback.html`;
    sessionStorage.setItem('dropbox_redirect_uri', redirectUri);
    const params = new URLSearchParams({
        client_id: DROPBOX_APP_KEY,
        response_type: 'code',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        redirect_uri: redirectUri,
        state: state,
        token_access_type: 'online',
        scope: 'files.metadata.read files.content.read',
    });
    const authUrl = `https://www.dropbox.com/oauth2/authorize?${params.toString()}`;
    return new Promise((resolve, reject) => {
        const width = Math.min(600, window.innerWidth - 40);
        const height = Math.min(700, window.innerHeight - 40);
        const left = (window.innerWidth - width) / 2 + (window.screenX || window.screenLeft || 0);
        const top = (window.innerHeight - height) / 2 + (window.screenY || window.screenTop || 0);
        const popup = window.open(authUrl, 'dropbox-oauth', `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`);
        if (!popup) {
            sessionStorage.setItem('dropbox_return_path', window.location.pathname);
            window.location.href = authUrl;
            return;
        }
        const handleMessage = (event) => {
            if (event.origin !== window.location.origin)
                return;
            if (event.data?.type !== 'dropbox-oauth-callback')
                return;
            window.removeEventListener('message', handleMessage);
            clearInterval(pollTimer);
            if (event.data.code && event.data.state) {
                sessionStorage.setItem('dropbox_oauth_result', JSON.stringify({ code: event.data.code, state: event.data.state }));
                resolve({ code: event.data.code, state: event.data.state });
            }
            else {
                reject(new Error('Dropbox authentication cancelled'));
            }
        };
        window.addEventListener('message', handleMessage);
        const pollTimer = setInterval(() => {
            if (popup.closed) {
                clearInterval(pollTimer);
                window.removeEventListener('message', handleMessage);
                const result = getDropboxOAuthResult();
                if (result) {
                    resolve(result);
                }
                else {
                    reject(new Error('Dropbox authentication cancelled'));
                }
            }
        }, 500);
    });
}
export function getDropboxOAuthResult() {
    const raw = sessionStorage.getItem('dropbox_oauth_result');
    if (!raw)
        return null;
    try {
        return JSON.parse(raw);
    }
    catch {
        return null;
    }
}
export function clearDropboxOAuthResult() {
    sessionStorage.removeItem('dropbox_oauth_result');
}
export async function exchangeDropboxCode(code) {
    const codeVerifier = sessionStorage.getItem('dropbox_pkce_verifier');
    const redirectUri = sessionStorage.getItem('dropbox_redirect_uri');
    if (!codeVerifier || !redirectUri) {
        throw new Error('Missing PKCE verifier or redirect URI');
    }
    const body = new URLSearchParams({
        code,
        grant_type: 'authorization_code',
        client_id: '6yfyq1hblo01sn3',
        redirect_uri: redirectUri,
        code_verifier: codeVerifier,
    });
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    let response;
    try {
        response = await fetch('/dropbox-api/oauth2/token', {
            method: 'POST',
            headers,
            body,
        });
        const ct = response.headers.get('content-type') || '';
        if (response.status === 404 || response.status === 502 || response.status === 503 || ct.includes('text/html')) {
            throw new Error('Proxy not available');
        }
    }
    catch (proxyErr) {
        console.warn('[Dropbox] Token proxy unavailable, trying direct:', proxyErr);
        response = await fetch('https://api.dropboxapi.com/oauth2/token', {
            method: 'POST',
            headers,
            body,
        });
    }
    if (!response.ok) {
        let errText = '';
        try {
            errText = await response.text();
        }
        catch { }
        console.error('[Dropbox] Token exchange error:', response.status, errText);
        throw new Error(`Token exchange failed: ${response.status}${errText ? ' - ' + errText.substring(0, 200) : ''}`);
    }
    const data = await response.json();
    sessionStorage.removeItem('dropbox_pkce_verifier');
    sessionStorage.removeItem('dropbox_pkce_state');
    sessionStorage.removeItem('dropbox_redirect_uri');
    sessionStorage.setItem('dropbox_access_token', data.access_token);
    return data.access_token;
}
export function getDropboxAccessToken() {
    return sessionStorage.getItem('dropbox_access_token');
}
export function clearDropboxAccessToken() {
    sessionStorage.removeItem('dropbox_access_token');
}
export async function listDropboxFolder(token, path = '') {
    const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
    const body = JSON.stringify({ path, recursive: false });
    let response;
    try {
        response = await fetch('/dropbox-api/2/files/list_folder', {
            method: 'POST',
            headers,
            body,
        });
        const ct = response.headers.get('content-type') || '';
        if (response.status === 404 || response.status === 502 || response.status === 503 || ct.includes('text/html')) {
            throw new Error('Proxy not available');
        }
    }
    catch (proxyErr) {
        console.warn('[Dropbox] Proxy unavailable, trying direct API:', proxyErr);
        response = await fetch('https://api.dropboxapi.com/2/files/list_folder', {
            method: 'POST',
            headers,
            body,
        });
    }
    if (!response.ok) {
        if (response.status === 401) {
            clearDropboxAccessToken();
            throw new Error('Dropbox session expired. Please reconnect.');
        }
        let errDetail = '';
        try {
            errDetail = await response.text();
        }
        catch { }
        console.error('[Dropbox] list_folder error:', response.status, errDetail);
        throw new Error(`Failed to list files: ${response.status}${errDetail ? ' - ' + errDetail.substring(0, 200) : ''}`);
    }
    const data = await response.json();
    return data.entries;
}
export async function downloadDropboxFile(token, path, fileName) {
    const headers = {
        'Authorization': `Bearer ${token}`,
        'Dropbox-API-Arg': JSON.stringify({ path }),
    };
    let response;
    try {
        response = await fetch('/dropbox-content/2/files/download', {
            method: 'POST',
            headers,
        });
        const ct = response.headers.get('content-type') || '';
        if (response.status === 404 || response.status === 502 || response.status === 503 || ct.includes('text/html')) {
            throw new Error('Proxy not available');
        }
    }
    catch (proxyErr) {
        console.warn('[Dropbox] Content proxy unavailable, trying direct:', proxyErr);
        response = await fetch('https://content.dropboxapi.com/2/files/download', {
            method: 'POST',
            headers,
        });
    }
    if (!response.ok) {
        if (response.status === 401) {
            clearDropboxAccessToken();
            throw new Error('Dropbox session expired. Please reconnect.');
        }
        throw new Error(`Failed to download ${fileName}: ${response.status}`);
    }
    const blob = await response.blob();
    return new File([blob], fileName, {
        type: blob.type || 'application/octet-stream',
        lastModified: Date.now(),
    });
}
let gapiPickerLoaded = false;
let googleAccessToken = null;
let cachedTokenClient = null;
let pendingTokenResolve = null;
let pendingTokenReject = null;
function loadGapiPicker() {
    return new Promise((resolve, reject) => {
        if (gapiPickerLoaded) {
            resolve();
            return;
        }
        if (!window.gapi) {
            reject(new Error('Google API not loaded'));
            return;
        }
        window.gapi.load('picker', {
            callback: () => { gapiPickerLoaded = true; resolve(); },
            onerror: () => reject(new Error('Failed to load Google Picker')),
        });
    });
}
function getOrCreateTokenClient() {
    if (cachedTokenClient)
        return cachedTokenClient;
    const clientId = process.env.GOOGLE_OAUTH_CLIENT_ID;
    if (!clientId || !window.google?.accounts?.oauth2)
        return null;
    console.log('[CloudImport] Creating Google OAuth token client (one-time)');
    cachedTokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'https://www.googleapis.com/auth/drive.readonly',
        callback: (response) => {
            if (response.error) {
                console.error('[CloudImport] OAuth error:', response.error, response.error_description);
                pendingTokenReject?.(new Error(response.error_description || response.error));
            }
            else {
                googleAccessToken = response.access_token;
                try {
                    sessionStorage.setItem('gdrive_access_token', response.access_token);
                }
                catch { }
                pendingTokenResolve?.(response.access_token);
            }
            pendingTokenResolve = null;
            pendingTokenReject = null;
        },
        error_callback: (error) => {
            console.error('[CloudImport] OAuth error_callback:', JSON.stringify(error));
            pendingTokenReject?.(new Error(error?.type || error?.message || 'OAuth flow error'));
            pendingTokenResolve = null;
            pendingTokenReject = null;
        },
    });
    return cachedTokenClient;
}
function getGoogleAccessToken() {
    return new Promise((resolve, reject) => {
        if (!window.google?.accounts?.oauth2) {
            reject(new Error('Google Identity Services not loaded'));
            return;
        }
        const tokenClient = getOrCreateTokenClient();
        if (!tokenClient) {
            reject(new Error('Failed to create Google token client'));
            return;
        }
        pendingTokenResolve = resolve;
        pendingTokenReject = reject;
        const timeoutId = setTimeout(() => {
            if (pendingTokenReject) {
                console.error('[CloudImport] OAuth timeout after 120s');
                pendingTokenReject(new Error('Google authentication timed out. Please try again.'));
                pendingTokenResolve = null;
                pendingTokenReject = null;
            }
        }, 120000);
        const origResolve = pendingTokenResolve;
        const origReject = pendingTokenReject;
        pendingTokenResolve = (token) => { clearTimeout(timeoutId); origResolve(token); };
        pendingTokenReject = (err) => { clearTimeout(timeoutId); origReject(err); };
        try {
            console.log('[CloudImport] Requesting access token...');
            tokenClient.requestAccessToken({ prompt: googleAccessToken ? '' : 'consent' });
        }
        catch (err) {
            clearTimeout(timeoutId);
            console.error('[CloudImport] requestAccessToken threw:', err);
            pendingTokenResolve = null;
            pendingTokenReject = null;
            reject(err);
        }
    });
}
export function isGoogleDriveAvailable() {
    return !!(window.gapi && window.google?.accounts?.oauth2);
}
export function openGoogleDrivePicker() {
    return new Promise(async (resolve) => {
        try {
            console.log('[CloudImport] Loading GAPI Picker...');
            await loadGapiPicker();
            console.log('[CloudImport] GAPI Picker loaded, requesting token...');
            const accessToken = await getGoogleAccessToken();
            console.log('[CloudImport] Token obtained, opening Picker...');
            const apiKey = process.env.GOOGLE_PICKER_API_KEY;
            const appId = process.env.GOOGLE_APP_ID;
            if (!apiKey || !appId) {
                console.error('[CloudImport] Google API Key or App ID not configured');
                resolve(null);
                return;
            }
            const view = new window.google.picker.View(window.google.picker.ViewId.DOCS);
            const picker = new window.google.picker.PickerBuilder()
                .setDeveloperKey(apiKey)
                .setAppId(appId)
                .setOAuthToken(accessToken)
                .addView(view)
                .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
                .setCallback((data) => {
                if (data.action === window.google.picker.Action.PICKED) {
                    const docs = data.docs || [];
                    const files = docs.map((doc) => ({
                        id: doc.id,
                        name: doc.name,
                        mimeType: doc.mimeType,
                        sizeBytes: doc.sizeBytes || 0,
                    }));
                    console.log('[CloudImport] Google Drive files selected:', files.length);
                    resolve(files);
                }
                else if (data.action === window.google.picker.Action.CANCEL) {
                    console.log('[CloudImport] Google Drive picker cancelled');
                    resolve(null);
                }
            })
                .build();
            picker.setVisible(true);
        }
        catch (err) {
            console.error('[CloudImport] Google Drive picker error:', err);
            resolve(null);
        }
    });
}
const GOOGLE_WORKSPACE_EXPORT_MAP = {
    'application/vnd.google-apps.document': {
        exportMimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        extension: '.docx',
    },
    'application/vnd.google-apps.spreadsheet': {
        exportMimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        extension: '.xlsx',
    },
    'application/vnd.google-apps.presentation': {
        exportMimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        extension: '.pptx',
    },
    'application/vnd.google-apps.drawing': {
        exportMimeType: 'application/pdf',
        extension: '.pdf',
    },
};
const DEFAULT_GOOGLE_WORKSPACE_EXPORT = {
    exportMimeType: 'application/pdf',
    extension: '.pdf',
};
export function isGoogleWorkspaceFile(mimeType) {
    return mimeType.startsWith('application/vnd.google-apps.');
}
export function getGoogleWorkspaceExportInfo(mimeType) {
    return GOOGLE_WORKSPACE_EXPORT_MAP[mimeType] || DEFAULT_GOOGLE_WORKSPACE_EXPORT;
}
export function fixGoogleWorkspaceFileName(fileName, mimeType) {
    if (!isGoogleWorkspaceFile(mimeType))
        return fileName;
    const exportInfo = getGoogleWorkspaceExportInfo(mimeType);
    if (!fileName.endsWith(exportInfo.extension)) {
        return fileName + exportInfo.extension;
    }
    return fileName;
}
export async function downloadGoogleDriveFile(fileId, accessToken, mimeType) {
    const isWorkspaceFile = mimeType && isGoogleWorkspaceFile(mimeType);
    const exportInfo = isWorkspaceFile ? getGoogleWorkspaceExportInfo(mimeType) : null;
    const proxyBase = '/gdrive-proxy';
    const directBase = 'https://www.googleapis.com';
    const buildUrl = (base) => {
        if (isWorkspaceFile && exportInfo) {
            return `${base}/drive/v3/files/${fileId}/export?mimeType=${encodeURIComponent(exportInfo.exportMimeType)}&supportsAllDrives=true`;
        }
        return `${base}/drive/v3/files/${fileId}?alt=media&supportsAllDrives=true&acknowledgeAbuse=true`;
    };
    const headers = { 'Authorization': `Bearer ${accessToken}` };
    // Try proxy first (dev), fall back to direct (production)
    try {
        const proxyUrl = buildUrl(proxyBase);
        console.log('[CloudImport] Trying Google Drive download via proxy:', proxyUrl);
        const response = await fetch(proxyUrl, { headers });
        const ct = response.headers.get('content-type') || '';
        if (response.status === 404 || response.status === 502 || response.status === 503 || ct.includes('text/html')) {
            throw new Error('Proxy not available');
        }
        if (!response.ok) {
            let errorDetail = '';
            try {
                const errorBody = await response.text();
                errorDetail = errorBody.substring(0, 500);
                console.error('[CloudImport] Google Drive proxy download error - HTTP', response.status, '| body:', errorDetail);
            }
            catch { }
            throw new Error(`HTTP ${response.status}${errorDetail ? ' - ' + errorDetail : ''}`);
        }
        return response.blob();
    }
    catch (proxyErr) {
        const errMsg = proxyErr instanceof Error ? proxyErr.message : String(proxyErr);
        if (errMsg.includes('Proxy not available') || errMsg.includes('Failed to fetch') || errMsg.includes('NetworkError') || errMsg.includes('TypeError')) {
            console.warn('[CloudImport] Proxy unavailable, trying direct Google API:', errMsg);
            const directUrl = buildUrl(directBase);
            console.log('[CloudImport] Trying Google Drive download directly:', directUrl);
            const response = await fetch(directUrl, { headers });
            if (!response.ok) {
                let errorDetail = '';
                try {
                    const errorBody = await response.text();
                    errorDetail = errorBody.substring(0, 500);
                    console.error('[CloudImport] Google Drive direct download error - HTTP', response.status, '| body:', errorDetail);
                }
                catch { }
                throw new Error(`Failed to download file: HTTP ${response.status}${errorDetail ? ' - ' + errorDetail : ''}`);
            }
            return response.blob();
        }
        throw new Error(`Failed to download file: ${errMsg}`);
    }
}
export function getGoogleDriveAccessToken() {
    return googleAccessToken;
}
export async function ensureGoogleAccessToken() {
    if (googleAccessToken)
        return googleAccessToken;
    const cached = sessionStorage.getItem('gdrive_access_token');
    if (cached) {
        googleAccessToken = cached;
        return cached;
    }
    return getGoogleAccessToken();
}
export async function listGoogleDriveFiles(accessToken, folderId = 'root') {
    const q = encodeURIComponent(`'${folderId}' in parents and trashed=false`);
    const fields = encodeURIComponent('files(id,name,mimeType,size,thumbnailLink,iconLink,parents,modifiedTime)');
    const params = `pageSize=100&fields=${fields}&q=${q}`;
    const headers = { 'Authorization': `Bearer ${accessToken}` };
    let response;
    try {
        response = await fetch(`/gdrive-proxy/drive/v3/files?${params}`, { headers });
        const ct = response.headers.get('content-type') || '';
        if (response.status === 404 || response.status === 502 || response.status === 503 || ct.includes('text/html')) {
            throw new Error('Proxy not available');
        }
    }
    catch (proxyErr) {
        console.warn('[CloudImport] GDrive proxy unavailable, trying direct:', proxyErr);
        response = await fetch(`https://www.googleapis.com/drive/v3/files?${params}`, { headers });
    }
    if (!response.ok) {
        if (response.status === 401) {
            googleAccessToken = null;
            try {
                sessionStorage.removeItem('gdrive_access_token');
            }
            catch { }
            throw new Error('Google Drive session expired. Please reconnect.');
        }
        let errDetail = '';
        try {
            errDetail = await response.text();
        }
        catch { }
        throw new Error(`Failed to list Google Drive files: ${response.status}${errDetail ? ' - ' + errDetail.substring(0, 200) : ''}`);
    }
    const data = await response.json();
    return data.files || [];
}
